import { useQueryClient } from "@tanstack/react-query";

import { useGetCustomHeaders } from "@vega-api/custom/stores/useGetCustomHeaders";
import {
  getGetFundCasSettingsByFiltersQueryKey,
  useGetFundCasSettingsByFundId,
  useUpdateFundCasSettingsByFundId,
} from "@vega-api/generated/cas-settings-v1/cas-settings-v1";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";

export const useCasVisibility = (fundId?: string) => {
  const queryClient = useQueryClient();
  const showToast = useToast();

  const { impersonatedUserId, limitedPartnerId } = useGetCustomHeaders();

  const {
    data: casSettings,
    isLoading: isLoadingSettings,
    error: settingsError,
    queryKey: casSettingsQueryKey,
  } = useGetFundCasSettingsByFundId(
    fundId || "",
    { impersonatedUserId, limitedPartnerId },
    {
      query: {
        enabled: Boolean(fundId),
      },
    },
  );

  const updateCasSettingsMutation = useUpdateFundCasSettingsByFundId({
    mutation: {
      onSuccess: () => {
        if (fundId) {
          queryClient.invalidateQueries({
            queryKey: casSettingsQueryKey,
          });
        }
        queryClient.invalidateQueries({
          queryKey: getGetFundCasSettingsByFiltersQueryKey(),
        });
      },
    },
  });

  const isActive = casSettings?.data?.isActive || false;
  const currentTemplateKey = casSettings?.data?.templateKey;
  const isUpdating = updateCasSettingsMutation.isPending;

  const updateVisibility = (isVisible: boolean, templateKey?: string) => {
    const finalTemplateKey = templateKey || currentTemplateKey;

    if (!fundId || !finalTemplateKey) {
      showToast({
        variant: "destructive",
        title: m.settings_casManagement_toast_visibilityUpdateFailed_title(),
        description:
          m.settings_casManagement_toast_visibilityUpdateFailed_description(),
      });

      return;
    }

    updateCasSettingsMutation.mutate({
      fundId,
      data: {
        templateKey: finalTemplateKey,
        isActive: isVisible,
      },
    });
  };

  return {
    casSettings: casSettings?.data,
    isActive,
    currentTemplateKey,
    isLoadingSettings,
    isUpdating,
    updateVisibility,
    settingsError,
  };
};
